import { useMemo, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useOrgQuery } from '@/lib/hooks/useOrgQuery'
import {
  Activity,
  Boxes,
  Database,
  MoreHorizontal,
  Plus,
  Search,
} from 'lucide-react'
import { toast } from 'sonner'
import { apiFetch, apiJson, orgPath } from '@/lib/api/client'
import { formatDate } from '@/lib/utils'
import { PageHeader } from '@/components/page-header'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  BigNumber,
  EcosystemMark,
} from '@/components/hawk-primitives'
import { RepoConnect } from '@/components/repo-connect'
import type { ClientSetup } from '@/lib/types/setups'
import { useAuth } from '@/lib/auth/useAuth'

interface Repo {
  name: string
  ecosystem: string
  upstream_base: string | null
  effective_upstream_base: string
  mount_path: string
  package_count: number
  last_fetched_at: string | null
  setups: ClientSetup[]
}

interface EcosystemInfo {
  name: string
  default_upstream_base: string
  setups: ClientSetup[]
}

export const Route = createFileRoute('/_app/org/repositories/')({
  component: RepositoriesPage,
})

function RepositoriesPage() {
  const { currentOrgSlug } = useAuth()
  const repos = useOrgQuery<Repo[]>(['admin-repositories'], '/admin/repositories', {
    refetchInterval: 30_000,
    staleTime: 25_000,
  })
  const ecosystems = useOrgQuery<EcosystemInfo[]>(['admin-ecosystems'], '/admin/ecosystems')

  const [query, setQuery] = useState('')
  const [ecoFilter, setEcoFilter] = useState<string>('all')
  const [createOpen, setCreateOpen] = useState(false)

  const filtered = useMemo(() => {
    const all = repos.data ?? []
    return all.filter((r) => {
      if (ecoFilter !== 'all' && r.ecosystem !== ecoFilter) return false
      if (!query) return true
      const q = query.toLowerCase()
      return (
        r.name.toLowerCase().includes(q) ||
        r.effective_upstream_base.toLowerCase().includes(q) ||
        r.ecosystem.toLowerCase().includes(q)
      )
    })
  }, [repos.data, query, ecoFilter])

  const kpis = useMemo(() => {
    const all = repos.data ?? []
    const now = Date.now()
    const dayMs = 24 * 60 * 60 * 1000
    const totalPackages = all.reduce((acc, r) => acc + r.package_count, 0)
    const activeRecent = all.filter(
      (r) =>
        r.last_fetched_at &&
        now - new Date(r.last_fetched_at).getTime() <= dayMs,
    ).length
    return {
      total: all.length,
      activeRecent,
      totalPackages,
    }
  }, [repos.data])

  return (
    <div className="px-10 py-8">
      <PageHeader
        title="Repositories"
        description="Mount points that proxy upstream ecosystems and cache install artifacts."
        right={
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="size-4" />
                New repository
              </Button>
            </DialogTrigger>
            <CreateRepositoryDialog
              ecosystems={ecosystems.data ?? []}
              onCreated={() => setCreateOpen(false)}
            />
          </Dialog>
        }
      />

      <KpiBand kpis={kpis} loaded={!repos.isLoading} />

      <div className="flex items-center gap-3 mt-10 mb-5 flex-wrap">
        <div className="relative flex-1 min-w-[240px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground/70 pointer-events-none" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, upstream, ecosystem..."
            className="pl-9 bg-secondary border-transparent focus:border-primary/30"
          />
        </div>
        <Select value={ecoFilter} onValueChange={setEcoFilter}>
          <SelectTrigger
            className="w-48 bg-secondary border-transparent"
            aria-label="Filter by ecosystem"
          >
            <SelectValue placeholder="All ecosystems" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All ecosystems</SelectItem>
            {(ecosystems.data ?? []).map((eco) => (
              <SelectItem key={eco.name} value={eco.name}>
                {eco.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="ml-auto font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
          {filtered.length.toString().padStart(2, '0')} / {(repos.data ?? []).length.toString().padStart(2, '0')} shown
        </div>
      </div>

      {repos.isLoading ? (
        <div className="border border-border bg-card/30 rounded-md h-[280px] hawk-shimmer" />
      ) : filtered.length === 0 ? (
        <div className="border border-border border-dashed rounded-md py-16 text-center bg-card/30">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-2">
            {repos.data?.length === 0 ? 'Empty' : 'No matches'}
          </div>
          <p className="text-sm text-muted-foreground">
            {repos.data?.length === 0
              ? 'No repositories configured. Create one to start proxying.'
              : 'Try a different search or filter.'}
          </p>
        </div>
      ) : (
        <div className="border border-border bg-card rounded-md overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-border bg-background/30">
                <TableHead className="w-14 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground" />
                <TableHead className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                  Repository
                </TableHead>
                <TableHead className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground text-right">
                  Packages
                </TableHead>
                <TableHead className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                  Last admission
                </TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((repo, i) => (
                <RepoRow
                  key={repo.name}
                  repo={repo}
                  delayMs={Math.min(i * 25, 200)}
                />
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  )
}

function KpiBand({
  kpis,
  loaded,
}: {
  kpis: {
    total: number
    activeRecent: number
    totalPackages: number
  }
  loaded: boolean
}) {
  const cards = [
    {
      label: 'Repositories',
      value: loaded ? kpis.total.toString() : '—',
      sub: 'configured',
      icon: Database,
    },
    {
      label: 'Active / 24h',
      value: loaded ? kpis.activeRecent.toString() : '—',
      sub:
        kpis.activeRecent > 0
          ? `of ${kpis.total} repositories`
          : 'no recent admissions',
      icon: Activity,
    },
    {
      label: 'Packages indexed',
      value: loaded ? kpis.totalPackages.toLocaleString() : '—',
      sub: kpis.totalPackages > 0 ? 'in this org' : 'awaiting sync',
      icon: Boxes,
    },
  ]

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 border border-border rounded-md bg-card/40 overflow-hidden">
      {cards.map((card, i) => {
        const Icon = card.icon
        return (
          <div
            key={card.label}
            className="relative px-6 py-6 animate-hawk-rise border-b border-r border-border last:border-r-0 sm:border-b-0"
            style={{ animationDelay: `${i * 60}ms` }}
          >
            <div className="flex items-center justify-between mb-4">
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                {card.label}
              </span>
              <Icon className="size-3.5 text-muted-foreground/50" strokeWidth={1.5} />
            </div>
            <BigNumber value={card.value} className="text-[44px] mb-2" />
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
              {card.sub}
            </div>
          </div>
        )
      })}
    </div>
  )
}

function RepoRow({ repo, delayMs }: { repo: Repo; delayMs: number }) {
  const queryClient = useQueryClient()
  const { currentOrgSlug } = useAuth()
  const [deleteOpen, setDeleteOpen] = useState(false)

  const del = useMutation({
    mutationFn: async () => {
      const r = await apiFetch(orgPath(`/admin/repositories/${repo.name}`), {
        method: 'DELETE',
      })
      if (!r.ok) throw new Error(await r.text())
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-repositories', currentOrgSlug] })
      queryClient.invalidateQueries({ queryKey: ['admin-stats', currentOrgSlug] })
      toast.success(`Repository ${repo.name} deleted`)
      setDeleteOpen(false)
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const [connectOpen, setConnectOpen] = useState(false)
  const base = `${window.location.origin}${repo.mount_path}`

  return (
    <TableRow
      className="group relative border-border hover:bg-popover animate-hawk-rise"
      style={{ animationDelay: `${delayMs}ms` }}
    >
      <TableCell className="w-14">
        <EcosystemMark ecosystem={repo.ecosystem} size="md" />
      </TableCell>
      <TableCell>
        <div className="text-sm font-medium">{repo.name}</div>
        <div className="font-mono text-[11px] text-muted-foreground truncate max-w-md mt-0.5">
          {repo.effective_upstream_base}
          {!repo.upstream_base && (
            <span className="ml-1.5 text-muted-foreground/60">(default)</span>
          )}
        </div>
      </TableCell>
      <TableCell className="text-right font-mono text-xs tabular-nums">
        {repo.package_count.toLocaleString()}
      </TableCell>
      <TableCell className="font-mono text-xs text-muted-foreground tabular-nums">
        {repo.last_fetched_at ? formatDate(repo.last_fetched_at) : 'never'}
      </TableCell>
      <TableCell>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon-sm" className="text-muted-foreground">
              <MoreHorizontal className="size-4" />
              <span className="sr-only">Open menu</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              disabled={(repo.setups?.length ?? 0) === 0}
              onClick={() => setConnectOpen(true)}
            >
              Connect instructions
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              variant="destructive"
              onClick={() => setDeleteOpen(true)}
            >
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <Sheet open={connectOpen} onOpenChange={setConnectOpen}>
          <SheetContent side="right" className="sm:max-w-lg">
            <SheetHeader className="px-5 pt-5 pb-4 border-b border-border bg-gradient-to-b from-card to-card/40 gap-3">
              <div className="flex items-center gap-3">
                <EcosystemMark ecosystem={repo.ecosystem} size="md" />
                <div className="min-w-0 flex-1">
                  <SheetTitle className="text-base tracking-tight truncate">
                    Connect to {repo.name}
                  </SheetTitle>
                  <SheetDescription className="text-xs mt-0.5">
                    Configure clients to use this repo as their index/registry.
                  </SheetDescription>
                </div>
              </div>
            </SheetHeader>
            <div className="flex-1 overflow-y-auto px-5 py-6">
              <RepoConnect
                setups={repo.setups}
                base={base}
                orgSlug={currentOrgSlug ?? ''}
                repoName={repo.name}
              />
            </div>
          </SheetContent>
        </Sheet>

        <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete {repo.name}?</DialogTitle>
              <DialogDescription>
                The repository will be unmounted. Cached files remain in storage
                and packages stay in the database, but the proxy endpoint stops
                serving until a repository with this name is recreated.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                disabled={del.isPending}
                onClick={() => del.mutate()}
              >
                {del.isPending ? 'Deleting...' : 'Delete'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </TableCell>
    </TableRow>
  )
}

function CreateRepositoryDialog({
  ecosystems,
  onCreated,
}: {
  ecosystems: EcosystemInfo[]
  onCreated: () => void
}) {
  const queryClient = useQueryClient()
  const { currentOrgSlug } = useAuth()
  const [name, setName] = useState('')
  const [ecosystem, setEcosystem] = useState('')
  const [upstream, setUpstream] = useState('')
  const [error, setError] = useState<string | null>(null)
  const defaultUpstream =
    ecosystems.find((e) => e.name === ecosystem)?.default_upstream_base ?? ''

  const create = useMutation({
    mutationFn: async () => {
      const r = await apiFetch(orgPath('/admin/repositories'), {
        method: 'POST',
        body: JSON.stringify({
          name,
          ecosystem,
          upstream_base: upstream.trim() || null,
        }),
      })
      if (!r.ok) {
        const body = await r.json().catch(() => ({ error: r.statusText }))
        throw new Error(body.error ?? `HTTP ${r.status}`)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-repositories', currentOrgSlug] })
      queryClient.invalidateQueries({ queryKey: ['admin-stats', currentOrgSlug] })
      toast.success(`Repository ${name} created`)
      setName('')
      setEcosystem('')
      setUpstream('')
      onCreated()
    },
    onError: (e: Error) => setError(e.message),
  })

  function submit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (!name) return setError('Name is required')
    if (!ecosystem) return setError('Ecosystem is required')
    create.mutate()
  }

  return (
    <DialogContent>
      <form onSubmit={submit}>
        <DialogHeader>
          <DialogTitle>New repository</DialogTitle>
          <DialogDescription>
            Mount a new proxy instance. The repository becomes available at{' '}
            <code className="font-mono text-foreground bg-secondary px-1.5 py-0.5 rounded">
              /repo/&lt;name&gt;
            </code>{' '}
            once created.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-5">
          <Field label="Name">
            <Input
              id="repo-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="pypi-public"
              autoFocus
            />
          </Field>

          <Field label="Ecosystem">
            <Select value={ecosystem} onValueChange={setEcosystem}>
              <SelectTrigger id="repo-eco">
                <SelectValue placeholder="Select..." />
              </SelectTrigger>
              <SelectContent>
                {ecosystems.map((eco) => (
                  <SelectItem key={eco.name} value={eco.name}>
                    {eco.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field label="Upstream URL">
            <Input
              id="repo-upstream"
              value={upstream}
              onChange={(e) => setUpstream(e.target.value)}
              placeholder={defaultUpstream || 'https://...'}
            />
            <p className="text-xs text-muted-foreground">
              {defaultUpstream
                ? <>Leave blank to use the public default <code className="font-mono">{defaultUpstream}</code>.</>
                : 'Pick an ecosystem first to see the default.'}
            </p>
          </Field>

          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>

        <DialogFooter>
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? 'Creating...' : 'Create repository'}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  )
}

function Field({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <div className="grid gap-2">
      <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
        {label}
      </label>
      {children}
    </div>
  )
}

